import * as React from 'react'

import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { AutoSizer, List } from 'react-virtualized'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import StruxhubTextValueSelector from '~/client/src/shared/components/StruxhubInputs/StruxhubSelector/StruxhubTextValueSelector'

export interface IFormGeneralTabDropdownOption {
  id: string
  name: string
  isDisabled?: boolean
}

interface IProps {
  selectedValue: string
  options: IFormGeneralTabDropdownOption[]

  shouldHighlightInvalid?: boolean
  isRequiredTextShown?: boolean
  isHighlighted?: boolean
  customSelectorLabel?: string
  targetClassName?: string
  itemRowClassName?: string

  onItemClick(stepId: string): void
}

const ROW_HEIGHT = 40
const MODAL_HEIGHT_RATIO = 0.4
const OVERSCAN_ROW_COUNT = 10

@observer
export default class FormGeneralOptionsDropdown extends React.Component<
  IProps,
  { isOpen: boolean }
> {
  public state = { isOpen: false }

  public render() {
    const {
      selectedValue,
      options,
      isHighlighted,
      customSelectorLabel,
      targetClassName,
      isRequiredTextShown,
      shouldHighlightInvalid,
    } = this.props

    const selectedValueName = options.find(o => o.id === selectedValue)?.name

    return (
      <Popover open={this.state.isOpen} onOpenChange={this.setOpen}>
        <PopoverTrigger
          nativeButton={false}
          render={
            <span
              className={classList({
                'tw:inline-block tw:grow-0 tw:align-middle': true,
                [targetClassName]: !!targetClassName,
              })}
            />
          }
        >
          {customSelectorLabel ? (
            <StruxhubTextValueSelector
              className="overflow-hidden no-flex"
              label={customSelectorLabel}
              isValid={!shouldHighlightInvalid || !!selectedValue}
              isRequired
              isRequiredTextHidden={!isRequiredTextShown}
              isHelperTextCondensed
              value={selectedValueName}
            />
          ) : (
            <div className="tw:flex tw:items-center pointer nowrap no-select">
              <span
                className={classList({
                  'text large grey-light tw:flex-1': true,
                  'blue-highlight': isHighlighted,
                })}
              >
                {selectedValueName}
              </span>
              <ChevronDownIcon className="tw:ml-1 tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
            </div>
          )}
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          className="tw:w-(--anchor-width) tw:min-w-40 tw:overflow-hidden tw:p-0"
        >
          {this.optionsModal}
        </PopoverContent>
      </Popover>
    )
  }

  private get optionsModal(): JSX.Element {
    const { options, selectedValue } = this.props

    return (
      <div className="col no-outline-container">
        <div className="virtualized-list-smart-wrapper">
          <AutoSizer disableHeight>
            {({ width }) => (
              <List
                data={options}
                width={width}
                height={Math.min(
                  window.innerHeight * MODAL_HEIGHT_RATIO,
                  ROW_HEIGHT * options.length,
                )}
                rowHeight={ROW_HEIGHT}
                rowCount={options.length}
                rowRenderer={this.renderRow}
                scrollToAlignment="start"
                scrollToIndex={options.findIndex(o => o.id === selectedValue)}
                overscanRowCount={OVERSCAN_ROW_COUNT}
              />
            )}
          </AutoSizer>
        </div>
      </div>
    )
  }

  private setOpen = (isOpen: boolean) => this.setState({ isOpen })

  private close = () => this.setOpen(false)

  private renderRow = ({ key, style, index }: any): JSX.Element => {
    const option = this.props.options[index]

    if (!option) {
      return null
    }

    const { itemRowClassName, selectedValue, onItemClick } = this.props

    const isSelected = selectedValue === option.id

    return (
      <div
        key={key}
        style={style}
        className={classList({
          'tw:flex tw:items-center highlighted-hover': true,
          'unclickable-element': isSelected,
          pointer: !isSelected,
        })}
        onClick={() => {
          onItemClick(option.id)
          this.close()
        }}
        title={option.name}
      >
        <div
          className={classList({
            'pl16 text line-24 ellipsis tw:flex-1': true,
            [itemRowClassName]: !!itemRowClassName,
            'extra-large': !itemRowClassName,
          })}
        >
          {option.name}
        </div>
        {isSelected && (
          <CheckIcon
            className={classList({
              'tw:mr-4 tw:size-4 tw:shrink-0 tw:text-primary': true,
              opacity5: option.isDisabled,
            })}
          />
        )}
      </div>
    )
  }
}
